import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Login from './components/Login';
import Products from './components/Products';
import AdminPanel from './components/AdminPanel';
import Cart from './components/Cart';
import CountrySelection from './components/CountrySelection';
import CartIcon from './components/CartIcon';
import LanguageSelector from './components/LanguageSelector';
import OrderSummary from './components/OrderSummary';
import { decodeHtmlEntities } from './utils/decodeHtmlEntities';

interface CompletedOrderData {
  orderId: number;
  username: string;
  items: { productId: number; quantity: number; name?: string; price?: number; image?: string }[];
  subtotal: number;
  shippingCost: number;
  total: number;
  shippingMethodTitle: string;
  shipping: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    postcode: string;
    email: string;
    phone: string;
  };
  billing?: {
    firstName: string;
    lastName: string;
    company?: string;
    nif?: string;
    address: string;
    city: string;
    state: string;
    postcode: string;
  };
  comments?: string;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [userRole, setUserRole] = useState<string | null>(localStorage.getItem('userRole'));
  const [selectedCountry, setSelectedCountry] = useState<string | null>(localStorage.getItem('selectedCountry'));
  const [selectedState, setSelectedState] = useState<string | null>(localStorage.getItem('selectedState'));
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<CompletedOrderData | null>(null);

  // Nom del país seleccionat, per mostrar-lo a la barra superior
  const { data: countriesForDisplay } = useQuery<{ code: string; name: string }[]>({
    queryKey: ['countries', i18n.language],
    queryFn: async () => {
      const response = await fetch(`/api/countries?lang=${i18n.language}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Error carregant països');
      }
      return response.json();
    },
    enabled: !!selectedCountry,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000
  });
  const selectedCountryNameRaw = countriesForDisplay?.find(c => c.code === selectedCountry)?.name;
  const selectedCountryName = selectedCountryNameRaw ? decodeHtmlEntities(selectedCountryNameRaw) : undefined;

  // Efecte per restaurar les dades d'ubicació si existeixen al localStorage
  useEffect(() => {
    const country = localStorage.getItem('selectedCountry');
    const state = localStorage.getItem('selectedState');
    
    if (country) setSelectedCountry(country);
    if (state) setSelectedState(state);
  }, []);

  // Afegir escoltador per l'event de reset complet
  useEffect(() => {
    const resetCountryHandler = () => {
      setSelectedCountry(null);
      setSelectedState(null);
    };

    // Registrar l'event personalitzat
    window.addEventListener('resetCountrySelection', resetCountryHandler);
    
    return () => {
      window.removeEventListener('resetCountrySelection', resetCountryHandler);
    };
  }, []);

  const handleLogin = (newToken: string, role: string) => {
    // Comencem sempre amb el carret buit: no ha de persistir mai entre sessions,
    // ni entre usuaris diferents ni amb el mateix usuari.
    localStorage.removeItem('cartItems');
    localStorage.removeItem('tempPostcode');
    localStorage.removeItem('tempCity');
    localStorage.setItem('token', newToken);
    localStorage.setItem('userRole', role);
    setToken(newToken);
    setUserRole(role);
    window.dispatchEvent(new Event('storageUpdated'));
    // Cap resultat d'enviament d'una sessió anterior ha de sobreviure al login.
    queryClient.removeQueries({ queryKey: ['shipping'] });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('selectedCountry');
    localStorage.removeItem('selectedState');
    localStorage.removeItem('tempPostcode');
    localStorage.removeItem('tempCity');
    // En tancar sessió buidem també el carret perquè no persisteixi entre usuaris
    localStorage.removeItem('cartItems');
    setToken(null);
    setUserRole(null);
    setSelectedCountry(null);
    setSelectedState(null);
    setCompletedOrder(null);
    // Notificar els components (p. ex. CartIcon) perquè reflecteixin el carret buit
    window.dispatchEvent(new Event('storageUpdated'));
    // Cap resultat d'enviament ha de sobreviure al tancament de sessió.
    queryClient.removeQueries({ queryKey: ['shipping'] });
    navigate('/');
  };

  const handleResetCart = () => {
    // Esborrar els items del carret
    localStorage.removeItem('cartItems');
    // Disparar event per notificar altres components
    window.dispatchEvent(new Event('storageUpdated'));
    // Reiniciar la selecció de país i estat
    localStorage.removeItem('selectedCountry');
    localStorage.removeItem('selectedState');
    localStorage.removeItem('tempPostcode');
    localStorage.removeItem('tempCity');
    setSelectedCountry(null);
    setSelectedState(null);
    setMenuOpen(false);
    setCompletedOrder(null);

    // Invalidar la cache de React Query de països, estats/províncies i productes
    // perquè es tornin a demanar al servidor en lloc de servir-se de la cache (1h de staleTime)
    queryClient.invalidateQueries({ queryKey: ['countries'] });
    queryClient.invalidateQueries({ queryKey: ['states'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    // Cap resultat d'enviament ha de sobreviure a un reset del carret.
    queryClient.removeQueries({ queryKey: ['shipping'] });
  };

  const handleCountryStateSelection = (countryCode: string, stateCode: string) => {
    setSelectedCountry(countryCode);
    setSelectedState(stateCode);
  };

  // Funció per tancar el menú quan es fa clic en una opció
  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Navega a l'inici i surt del resum de comanda si s'estava mostrant
  const goHome = () => {
    setCompletedOrder(null);
    navigate('/');
  };

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="sticky top-0 z-30 bg-black shadow-md print:hidden">
        <div className="max-w-7xl mx-auto flex justify-between items-center py-2 px-4">
          {/* Logo i boto menu */}
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="Logo"
              className="h-10 w-auto cursor-pointer"
              onClick={goHome}
            />
          </div>

          {/* Botons per a desktop */}
          <div className="hidden md:flex items-center space-x-3">
            {selectedCountryName && (
              <div
                className="flex items-center gap-1.5 h-9 text-gray-300 text-sm"
                title={t('menu.selectedCountry', { country: selectedCountryName })}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{selectedCountryName}</span>
              </div>
            )}
            <button
              onClick={goHome}
              className="flex items-center gap-2 px-3 bg-black hover:bg-gray-900 text-white rounded-lg h-9 border border-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>{t('menu.home')}</span>
            </button>


            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 px-3 bg-black hover:bg-gray-900 text-white rounded-lg h-9 border border-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <span>{t('menu.options')}</span>
            </button>
        <div className="h-9">
              <LanguageSelector />
            </div>
            <div className="h-9">
              <CartIcon countryCode={selectedCountry || ''} stateCode={selectedState || ''} />
            </div>
          </div>
          
          {/* Botó hamburguesa per a mòbils */}
          <div className="md:hidden flex items-center gap-2">
            <div className="h-9">
              <CartIcon countryCode={selectedCountry || ''} stateCode={selectedState || ''} />
            </div>
            
            <button 
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 text-white focus:outline-none"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Menú desplegable per a mòbils */}
        {menuOpen && (
          <div className="sm:hidden bg-black p-4 mt-2 rounded-md">
            <div className="flex flex-col space-y-3">
              {selectedCountryName && (
                <div
                  className="flex items-center gap-1.5 text-gray-300 text-sm px-2"
                  title={t('menu.selectedCountry', { country: selectedCountryName })}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{selectedCountryName}</span>
                </div>
              )}

              <LanguageSelector />

              <button
                onClick={() => {
                  goHome();
                  closeMenu();
                }}
                className="flex items-center bg-black hover:bg-gray-900 text-white p-2 rounded-md border border-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {t('menu.home')}
              </button>
              
              <button
                onClick={() => {
                  handleResetCart();
                  closeMenu();
                }}
                className="flex items-center bg-black hover:bg-gray-900 text-white p-2 rounded-md border border-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('cart.reset')}
              </button>
              
              {userRole === 'admin' && (
                <button
                  onClick={() => {
                    setCompletedOrder(null);
                    navigate('/admin');
                    closeMenu();
                  }}
                  className="flex items-center bg-black hover:bg-gray-900 text-white p-2 rounded-md border border-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  {t('menu.options')}
                </button>
              )}
              
              <button 
                onClick={() => {
                  handleLogout();
                  closeMenu();
                }}
                className="flex items-center bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('login.logout')}
              </button>
            </div>
          </div>
        )}        </nav>

        {/* Menú lateral d'usuari */}
        <div
          className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
            userMenuOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
          } print:hidden`}
          onClick={() => setUserMenuOpen(false)}
        />
        <div
          className={`fixed right-0 top-0 h-full w-72 bg-white z-50 transform transition-all duration-300 ease-out ${
            userMenuOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-full'
          } print:hidden`}
        >
          <div>
            <div>
              <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">{t('menu.options')}</h2>
                <button 
                  onClick={() => setUserMenuOpen(false)}
                  className="text-black"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="space-y-3 py-4 px-6">
              {userRole === 'admin' && (
                <button
                  onClick={() => {
                    setCompletedOrder(null);
                    navigate('/admin');
                    setUserMenuOpen(false);
                  }}
                  className="flex items-center w-full px-0 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                  {t('admin.title')}
                </button>
              )}
              <button 
                onClick={() => {
                  handleResetCart();
                  setUserMenuOpen(false);
                }}
                className="flex items-center w-full px-0 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('cart.reset')}
              </button>
              <button 
                onClick={() => {
                  handleLogout();
                  setUserMenuOpen(false);
                }}
                className="flex items-center w-full px-0 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('login.logout')}
              </button>
            </div>
          </div>
        </div>

      <main className="max-w-7xl mx-auto py-6 px-4">
        {completedOrder ? (
          <OrderSummary
            orderId={completedOrder.orderId}
            username={completedOrder.username}
            items={completedOrder.items}
            subtotal={completedOrder.subtotal}
            shippingCost={completedOrder.shippingCost}
            total={completedOrder.total}
            shippingMethodTitle={completedOrder.shippingMethodTitle}
            shipping={completedOrder.shipping}
            billing={completedOrder.billing}
            comments={completedOrder.comments}
            onLogout={handleLogout}
          />
        ) : (
          <Routes>
            {/* Ruta del panell d'administració accessible sempre que l'usuari sigui admin */}
            {userRole === 'admin' && (
              <Route path="/admin" element={<AdminPanel />} />
            )}

            {/* Ruta principal que requereix seleccionar país */}
            <Route path="/" element={
              !selectedCountry || !selectedState ? (
                <div className="flex justify-center">
                  <div className="w-full">
                    <CountrySelection onSelect={handleCountryStateSelection} />
                  </div>
                </div>
              ) : (
                <Products countryCode={selectedCountry} stateCode={selectedState} />
              )
            } />

            {/* Ruta del carret que requereix seleccionar país */}
            <Route path="/cart" element={
              !selectedCountry || !selectedState ? (
                <div className="flex justify-center">
                  <div className="w-full">
                    <CountrySelection onSelect={handleCountryStateSelection} />
                  </div>
                </div>
              ) : (
                <Cart countryCode={selectedCountry} stateCode={selectedState} onOrderComplete={setCompletedOrder} />
              )
            } />

            {/* Redirecció de qualsevol altra ruta a l'inici */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;
